import { getKeys } from '../DrillingOperationsTree/SetTreeKeys'
import { refreshEventName } from './initialStates'
import { cloneDeep } from 'lodash'

/**
 * This function sends a request to save a reference of the on the backend
 * @param dashboardId - id of the dashboard to be saved as a path on the backend
 * @param asset asset name
 * @param runReference an object that specifies the interventionId, drillingSectionPosIndex and runPosIndex
 */

export const saveRunReference = (dashboardId: number, asset: string, runReference: runReferenceProps): void => {
    fetch(
        `/services/plugin-drilling-operations-span-picker/tree/${dashboardId}/${asset}/plugin-drilling-operations-span-picker`,
        {
            method: 'post',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify([{ name: 'runPath', value: JSON.stringify(runReference) }])
        }
    )
}

/**
 * Get item of the tree based on a key
 *
 * @param key {React.Key[]} - the key of the searched item
 * @param treeItem {interventionDataTypes[]} - a formatted tree
 * @returns {runDataTypes | operationNodesTypes | null} - returns the item searched
 */

export const getTreeItem = (
    key: React.Key[],
    formattedTree: Array<interventionDataTypes>
): runDataTypes | operationNodesTypes | null => {
    let treeItem: runDataTypes | operationNodesTypes | null = null
    if (key && formattedTree) {
        var i
        for (i = 0; treeItem == null && i < formattedTree.length; i++) {
            treeItem = searchTree(key[0], formattedTree[i])
        }
    }
    return treeItem
}

/**
 * This recursive function goes through the tree, searching for an item with a defined key
 *
 * @param key {Ract.Key} - the key of the searched item
 * @param element {interventionDataTypes[] | sectionDataTypes[] | runDataTypes[] | operationNodesTypes[]} - the element that will be compared
 * or the parent of elements to be compared
 * @returns {operationNodesTypes | runDataTypes | null} - the item searched
 */

const searchTree = (
    key: React.Key[],
    element: interventionDataTypes[] | sectionDataTypes[] | runDataTypes[] | operationNodesTypes[]
): operationNodesTypes | runDataTypes | null => {
    if (key && element) {
        if (element.key === key) {
            return element
        } else if (element.children != null && element.children?.length > 0) {
            var i
            var result = null
            for (i = 0; result == null && i < element.children.length; i++) {
                result = searchTree(key, element.children[i])
            }
            return result
        }
    }
    return null
}

/**
 * This function receives the context of the dashboard and a timespan, and update the timespan of all the widgets
 *
 * @param context {any} - dashboard context
 * @param timespan {string} - timespan in a pipes format
 * @returns {void}
 */

const updateAllWidgets = (context: any, timespan: string): void => {
    context.dashboard.widgets.forEach((w: any, index: number) => context.actions.setTimespan(timespan, index, true))
}

/**
 * This function works to update the dashboard or the widget's span
 * @param operationsPickerConfigs - configurations that defines how the component will update the dashboard
 * @param context - the context of the dashboard, which provides methods to update the dashboard
 * @param timespan - the timespan to update the dashboard
 * @param asset - asset name, important to save on the back end the run reference
 * @param runReference - a reference to be saved on the backend, to be used on pipes queries
 * @param updateAllWidgets {Function} - received as a param, to help write the tests
 * @param saveRunReference {Function} - received as a param, to help write the tests
 */

export const updateSpan = (
    operationsPickerConfigs: OperationsPickerConfigsProps,
    context: any,
    timespan: string,
    runReference: runReferenceProps,
    headerContext: headerContextProps,
    updateAllWidgets: Function,
    saveRunReference: Function
): void => {
    let dashboardId = context?.dashboard?.id
    if (operationsPickerConfigs.saveRunReference && dashboardId)
        saveRunReference(dashboardId, headerContext.asset, runReference)

    if (operationsPickerConfigs.updateDashboardSpan) {
        headerContext.setTimespan(timespan, undefined, true)
    } else {
        updateAllWidgets(context, timespan)
    }
}

/**
 * This function alters the timespan of the identity lookup filter
 * @param context - the context of the dashboard provides the method to change the filters values
 * @param timespan - which will be on the filter
 */

export const updateIdentityLookup = (headerContext: headerContextProps, timespan: string[]) => {
    if (headerContext.context === 'widgetEditor') {
        headerContext.updateFilter('lookupValues', { identity: timespan })
    } else if (headerContext?.filterValues) {
        let filterValues = cloneDeep(headerContext.filterValues)
        filterValues.identity = timespan
        headerContext.setFilters(filterValues)
    }
}

/**
 * This function works the update of the dashboard/widgets and the identity lookup, depending on the configurations
 *
 * @param operationsPickerConfigs - configurations that defines how the component will update the dashboard
 * @param context - the context of the dashboard, which provides methods to update the dashboard
 * @param formattedTree {interventionDataTypes[]} - drilling operations tree formatted
 * @param selectedKey {string[]} - the selected key on the tree
 * @param selectedTimespan {string} - the timespan of the selected key
 * @param runReference {runReferenceProps} - a reference to be saved on the backend, to be used on pipes queries
 * @param checkedKeys {string[]} - an array with the keys of the checked items over the tree.
 * @param individualTimespans {boolean} - signs if the timespan of the dashboard will be updated, or widget by widget.
 * @param headerContext {headerContextProps} - items reccevid and grouped to be used in different functions.
 * @param refreshFunction - grouped functions passed as parameters to help writing unit tests.
 * @returns {void}
 */

export const updateSpanAndIdentity = (
    operationsPickerConfigs: OperationsPickerConfigsProps,
    context: any,
    formattedTree: interventionDataTypes[],
    selectedKey: string[],
    selectedTimespan: string,
    runReference: runReferenceProps,
    checkedKeys: string[],
    headerContext: headerContextProps,
    refreshFunctions: {
        dispatchRefreshEventFormattingDetails: Function
        updateSpan: Function
        updateIdentityLookup: Function
        getAllTheSpans: Function
        getMinMaxSpans: Function
        getAllTheSpansMinMax: Function
    }
) => {
    let checkedTimespan = []
    if (operationsPickerConfigs.enableChecking) {
        checkedTimespan =
            operationsPickerConfigs.timespanDefaultOption === 'individual'
                ? refreshFunctions.getAllTheSpans(
                      checkedKeys,
                      formattedTree,
                      operationsPickerConfigs.autoLoadsLastRunTillNow
                  )
                : operationsPickerConfigs.timespanDefaultOption === 'individualMinMax'
                ? refreshFunctions.getAllTheSpansMinMax(
                      checkedKeys,
                      formattedTree,
                      operationsPickerConfigs.autoLoadsLastRunTillNow
                  )
                : refreshFunctions.getMinMaxSpans(
                      checkedKeys,
                      formattedTree,
                      operationsPickerConfigs.autoLoadsLastRunTillNow
                  )
        refreshFunctions.updateIdentityLookup(headerContext, checkedTimespan)
    }
    refreshFunctions.updateSpan(
        operationsPickerConfigs,
        context,
        selectedTimespan,
        runReference,
        headerContext,
        updateAllWidgets,
        saveRunReference
    )
    refreshFunctions.dispatchRefreshEventFormattingDetails(
        operationsPickerConfigs,
        selectedKey,
        selectedTimespan,
        runReference,
        checkedKeys,
        formattedTree,
        checkedTimespan
    )
}

/**
 * This function provides the min and max timespan of a group of checked items, if the item is not
 * found on the tree, it will return a timespan like 'from ts 0 to ts 0', if the autoLoadsLastRunTillNow is true, it will return a timespan that ends in now
 *
 * @param checkedKeys - array of strings containing the checked items on the tree
 * @param formattedTree
 * @param autoLoadsLastRunTillNow
 * @returns {string[]} on the format to be used as span on live
 */

export const getMinMaxSpans = (
    checkedKeys: string[],
    formattedTree: interventionDataTypes[],
    autoLoadsLastRunTillNow: boolean
): string[] => {
    let startTimestamp = 0,
        endTimestamp = 0
    if (Array.isArray(checkedKeys) && Array.isArray(formattedTree))
        checkedKeys
            .filter(e => /^Operation|^Run/.test(e))
            .forEach(checkedKey => {
                let treeItem = getTreeItem([checkedKey], formattedTree)
                if (
                    treeItem &&
                    typeof treeItem.start_timestamp !== undefined &&
                    (startTimestamp === 0 || treeItem.start_timestamp < startTimestamp)
                )
                    startTimestamp = treeItem.start_timestamp
                if (
                    treeItem &&
                    typeof treeItem.end_timestamp !== undefined &&
                    (endTimestamp === 0 || treeItem.end_timestamp > endTimestamp)
                )
                    endTimestamp = treeItem.end_timestamp
            })
    return [timespanFromTimestamps(startTimestamp, endTimestamp, autoLoadsLastRunTillNow)]
}

export const getMinSpans = (formattedTree: interventionDataTypes[]): number => {
    const checkedKeys = getKeys(formattedTree)

    let startTimestamp = 0

    if (Array.isArray(checkedKeys) && Array.isArray(formattedTree))
        checkedKeys
            .filter(e => /^Operation|^Run/.test(e))
            .forEach(checkedKey => {
                let treeItem = getTreeItem([checkedKey], formattedTree)
                if (
                    treeItem &&
                    typeof treeItem.start_timestamp !== undefined &&
                    (startTimestamp === 0 || treeItem.start_timestamp < startTimestamp)
                )
                    startTimestamp = treeItem.start_timestamp
            })
    return startTimestamp
}

/**
 * This function provides an array of timespans related to an array of checked run/operations items
 *
 * @param checkedKeys - array of strings containing the checked items on the tree
 * @param formattedTree
 * @param untillNow {boolean} - represents if the timespan should end on now
 * @returns array of strings on the format to be used as span on live
 */

export const getAllTheSpans = (
    checkedKeys: string[],
    formattedTree: interventionDataTypes[],
    untillNow: boolean
): string[] => {
    let timespans: string[] = [],
        treeItem
    let sortedKeys: string[] = []
    if (Array.isArray(checkedKeys) && Array.isArray(formattedTree)){
        sortedKeys = checkedKeys.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))
        sortedKeys
            .filter(checkedKey => /^Operation|^Run/.test('' + checkedKey))
            .filter(checkedKey => (typeof getTreeItem([checkedKey], formattedTree)?.start_timestamp === 'number' && getTreeItem([checkedKey], formattedTree)?.startTimestamp !== 0))
            .forEach((checkedKey, index, array) => {
                const lastUntilNow = untillNow ? index == array.length - 1 : false
                treeItem = getTreeItem([checkedKey], formattedTree)
                if ((typeof treeItem?.start_timestamp === 'number' && treeItem?.startTimestamp !== 0) &&
                ((typeof treeItem?.end_timestamp === 'number' && treeItem?.end_timestamp !== 0) || lastUntilNow))
                    timespans.push(timespanFromTimestamps(treeItem?.start_timestamp, treeItem?.end_timestamp, lastUntilNow))
            })
    }
    if (timespans.length === 0)
        timespans.push('None')
    return timespans
}

/**
 * This function provides an array of timespans related to an array of checked run items
 *
 * @param checkedKeys - array of strings containing the checked items on the tree
 * @param formattedTree
 * @param untillNow {boolean} - represents if the timespan should end on now
 * @returns array of strings on the format to be used as span on live
 */

export const getAllTheSpansMinMax = (
    checkedKeys: string[],
    formattedTree: interventionDataTypes[],
    untillNow: boolean
): string[] => {
    let timespans: string[] = [],
        treeItem
    if (Array.isArray(checkedKeys) && Array.isArray(formattedTree))
        checkedKeys
            .filter(checkedKey => /^Run/.test('' + checkedKey))
            .forEach((checkedKey, index, array) => {
                const lastUntilNow = untillNow ? index == array.length - 1 : false
                treeItem = getTreeItem([checkedKey], formattedTree)
                timespans.push(timespanFromTimestamps(treeItem?.start_timestamp, treeItem?.end_timestamp, lastUntilNow))
            })
    return timespans
}

/**
 * This function provides a string equivalent to the timespan of given start and end timestamp
 *
 * @param startTimestamp number
 * @param endTimestamp number
 * @param untillNow boolean
 * @returns string representing the timespan of those timestamps
 */

export const timespanFromTimestamps = (
    startTimestamp: number | undefined,
    endTimestamp: number | undefined,
    untillNow: boolean
): string => {
    let res = ''
    if (!startTimestamp || typeof startTimestamp !== 'number') {
        res = 'from ts 0 to ts 0'
    } else {
        res = 'from ts ' + startTimestamp
        if (untillNow && typeof untillNow === 'boolean') {
            res += ' to now'
        } else {
            if (
                (endTimestamp === 0 && startTimestamp === 0) ||
                (startTimestamp !== 0 && endTimestamp !== 0 && typeof endTimestamp === 'number')
            )
                res += ' to ts ' + endTimestamp
        }
    }
    return res
}

/**
 * This function dispatches an event (when the timespan redresh is triggered) informing data about the update
 *
 * @param operationsPickerConfigs {O}
 * @param selectedKey
 * @param selectedTimespan
 * @param runReference
 * @param checkedKeys
 * @param formattedTree
 * @param checkedTimespans
 * @returns {void}
 */

export const dispatchRefreshEventFormattingDetails = (
    operationsPickerConfigs: OperationsPickerConfigsProps,
    selectedKey: React.Key[],
    selectedTimespan: string[],
    runReference: runReferenceProps,
    checkedKeys: React.Key[],
    formattedTree: interventionDataTypes[],
    checkedTimespans: string[]
): void => {
    let details: refreshEventProps = {
        operationsPickerConfigs: operationsPickerConfigs,
        selectedItem: getTreeItem(selectedKey, formattedTree),
        selectedTimespan: selectedTimespan,
        runReference: runReference,
        checkedItemsLabels: getCheckedItemsLabels(checkedKeys, formattedTree),
        checkedTimespans: checkedTimespans
    }
    dispatchRefreshEvent(details)
}

/**
 * Dispatch a defined event with passed details
 * @param details
 */

export const dispatchRefreshEvent = details => {
    const event = new CustomEvent(refreshEventName, {
        detail: details
    })
    document.dispatchEvent(event)
}

/**
 * This function returns an array of labels (labels are only present in Runs and Operations)
 * related to the keys that were provided, extracted from the formatted tree
 *
 * @param checkedKeys {string[]}
 * @param formattedTree {interventionDataTypes[]}
 * @returns {string[]}
 */

export const getCheckedItemsLabels = (checkedKeys: React.Key[], formattedTree: interventionDataTypes[]): string[] => {
    let res = []
    if (Array.isArray(checkedKeys) && Array.isArray(formattedTree)) {
        checkedKeys.forEach(checkedKey => res.push(getTreeItem([checkedKey], formattedTree)?.label))
    }
    return res.filter(e => e !== undefined)
}
